<template>
  <div
    :data-active-by-click="!isMobile"
    :style="{ ...initialSize, ...fileBoxSize, minHeight: headerHeight ? `${headerHeight}px` : 'unset' }"
    class="file-item-flow normal-bg"
  >
    <div
      v-if="!isImageFile"
      ref="headerElRef"
      :class="[
        coverLoaded && 'show-shadow',
      ]"
      class="header-info"
    >
      <img
        v-if="file.icon_link"
        :src="file.icon_link"
        class="header-info__icon"
        draggable="false"
      >
      <div class="header-info-box">
        <div class="header-info-box__name">
          {{ file.name }}
        </div>
        <div class="header-info-box__sub">
          <div v-if="!isFolder(file.kind)">
            {{ storageUnit(Number(file.size)) }}
          </div>
          <div class="modified-time ellipsis">
            <ClientOnly :placeholder="FILE_MODIFIED_TIME_PLACEHOLDER">
              {{ dayjs(file.modified_time).format(TIME_FORMAT_STRING) }}
            </ClientOnly>
          </div>
        </div>
      </div>
    </div>
    <ClientOnly>
      <div
        :style="{ minHeight: headerHeight ? `${headerHeight}px` : 'unset'}"
        class="tool-box"
      >
        <template v-if="isMobile">
          <div
            v-if="mobileSelectMode"
            class="checkbox"
            style="margin-left: auto;"
          >
            <FileUnCheckedIcon
              class="file-unchecked-icon"
              color="#A8B2BD"
              hover-able
              hover-color="#306EFF"
            />
            <FileCheckedIcon
              class="file-checked-icon"
              color="#306EFF"
            />
          </div>
          <!-- <div
            v-else
            class="menu"
            @click.stop="(e) => emits('clickShowContextMenu', e.target, file)"
          >
            <FileMenuIcon color="#A8B2BD" />
          </div> -->
        </template>
        <template v-else>
          <div
            class="checkbox"
            @click.stop="handleItemToggle"
          >
            <FileUnCheckedIcon
              class="file-unchecked-icon"
              color="#A8B2BD"
              hover-able
              hover-color="#306EFF"
            />
            <FileCheckedIcon
              class="file-checked-icon"
              color="#306EFF"
            />
          </div>
          <div
            class="menu"
            @click.stop="e => emits('clickShowContextMenu', e.target, file)"
          >
            <FileMenuIcon color="#A8B2BD" />
          </div>
        </template>
      </div>
      <FileCover
        v-if="file.thumbnail_link"
        :file-extension="file.file_extension"
        :icon-link="file.icon_link"
        :mime-type="file.mime_type"
        :name="file.name"
        :thumbnail-link="file.thumbnail_link"
        @load="handleFileCoverLoaded"
      />
      <!-- 兜底: 服务端没有返回缩略图的情况 -->
      <div
        v-else-if="isImageFile || isVideoFile"
        class="ssr-placeholder"
      />

      <template #fallback>
        <div
          v-if="!isFolder(file.kind)"
          class="ssr-placeholder"
        />
      </template>
    </ClientOnly>
    <img
      v-if="isVideoFile"
      class="player-icon"
      draggable="false"
      src="@/assets/images/share/player-icon.svg"
      @click="handlePlayIconClick"
    >
  </div>
</template>

<script
  lang="ts"
  setup
>
import {
  computed,
  ref,
} from '#imports'
import FileCheckedIcon from '@/components/icons/FileCheckedIcon.vue'
import FileMenuIcon from '@/components/icons/FileMenuIcon.vue'
import FileUnCheckedIcon from '@/components/icons/FileUnCheckedIcon.vue'
import FileCover from '@/components/share/FileCover.vue'
import {
  isImage,
  isVideo,
} from '@/utils'
import { useElementSize } from '@vueuse/core'
import dayjs from 'dayjs'
import type { CSSProperties } from 'vue'
import { useLayout } from '~/store/layout'
import { useShare } from '~/store/share'
import {
  FILE_MODIFIED_TIME_PLACEHOLDER,
  TIME_FORMAT_STRING,
} from '~/types/constants'
import { isFolder } from './share.script'

const THRESHOLD_WIDTH_MAX = 810
const THRESHOLD_WIDTH = 540
const THRESHOLD_WIDTH_MIN = 180
const LIMIT_RATE = 5 / 4

const emits = defineEmits<{
  clickShowContextMenu: [target: Element | null, file: PPFile],
  openFile: [file: PPFile]
}>()

const { file, mobileSelectMode } = defineProps<{ file: PPFile; mobileSelectMode: boolean }>()

const fileBoxSize = ref<CSSProperties>({})
const headerElRef = ref<HTMLElement>()
const coverLoaded = ref(false)
const shareStore = useShare()
const layout = useLayout()

const { height: headerHeight } = useElementSize(headerElRef, undefined, { box: 'border-box' })

const isMobile = computed(() => layout.isMobile)
const isImageFile = computed(() => isImage(file.file_extension, file.mime_type))
const isVideoFile = computed(() => isVideo(file.file_extension, file.mime_type))
const initialSize = computed<CSSProperties>(() => ({
  width: isMobile.value ? '100%' : '540px',
  height: (file.thumbnail_link || isImageFile.value || isVideoFile.value) ? '156px' : '60px',
}))

const handleFileCoverLoaded = (width?: number, height?: number) => {
  coverLoaded.value = true

  if (!width || !height) return

  if (isMobile.value) {
    if (width < window.innerWidth / 3) {
      return fileBoxSize.value = {
        width: '100%',
        height: `${height * 3}px`,
      }
    }
    fileBoxSize.value = {
      width: '100%',
      height: 'auto',
    }
    return
  }
  // 宽高比
  const rate = width / height

  if (width >= THRESHOLD_WIDTH_MAX / 3 && rate >= LIMIT_RATE) {
    // 宽度设置为 THRESHOLD_WIDTH_MAX 高度自动, 条件如下:
    // 1.图片文件
    // 2.宽/高比大于 LIMIT_RATE
    // 3.宽度大于 THRESHOLD_WIDTH,
    return fileBoxSize.value = {
      width: `${isImageFile.value ? THRESHOLD_WIDTH_MAX : THRESHOLD_WIDTH}px`,
      height: 'auto',
    }
  }

  // 小于 THRESHOLD_WIDTH_MIN, 将图片放大3倍
  if (width <= THRESHOLD_WIDTH_MIN) {
    return fileBoxSize.value = {
      width: `${THRESHOLD_WIDTH}px`,
      height: `${height * 3}px`,
    }
  }

  // @1 小于 THRESHOLD_WIDTH_MAX 的三分之一时, 等比缩放到 THRESHOLD_WIDTH
  // @2 竖图, 等比缩放到 THRESHOLD_WIDTH
  if (width <= THRESHOLD_WIDTH_MAX / 3 || rate < LIMIT_RATE) {
    return fileBoxSize.value = {
      width: `${THRESHOLD_WIDTH}px`,
      height: 'auto',
    }
  }

  // 宽度设为 THRESHOLD_WIDTH, 高度不变
  fileBoxSize.value = {
    width: `${THRESHOLD_WIDTH}px`,
    height: `${height}px`,
  }
}

const handleItemToggle = () => {
  shareStore.setSelectedIds([file.id], true)
}

const handlePlayIconClick = (e: MouseEvent): void => {
  if (mobileSelectMode || e.shiftKey || e.ctrlKey || e.metaKey) return

  e.stopPropagation()

  emits('openFile', file)
}

</script>

<style lang="less">
.checked .file-item-flow {
  .tool-box {
    display: flex;

    .checkbox {
      .file-unchecked-icon {
        display: none;
      }

      .file-checked-icon {
        display: block;
      }
    }
  }
}

.file-item-flow {
  --error-placeholder-image-size: 54px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  max-width: 100%;
  background-color: #e1e6ea;

  .ssr-placeholder {
    width: 100%;
    height: 100%;
    background: url(@/assets/images/share/thumbnail-placeholder.svg) no-repeat center / var(--error-placeholder-image-size), #e1e6ea;
  }

  .header-info {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    min-height: 60px;
    display: flex;
    gap: 12px;
    align-items: center;
    z-index: 1;
    padding: 11px 40px;

    &.show-shadow {
      background: linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 100%);

      .header-info-box__name {
        color: #ffffffe5;
      }

      .header-info-box__sub {
        color: #ffffffb2;
      }
    }

    &__icon {
      width: 24px;
      height: 24px;
    }

    &-box {
      display: flex;
      flex-direction: column;

      &__name {
        font-size: 15px;
        line-height: 22px;
        color: #000;

        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
        word-break: break-all;
      }

      &__sub {
        display: flex;
        gap: 8px;
        font-size: 11px;
        line-height: 16px;
        color: #666;
      }
    }
  }

  .tool-box {
    position: absolute;
    z-index: 5;
    left: 0;
    top: 0;
    width: 100%;
    height: 60px;
    display: none;
    align-items: center;
    padding: 11px 8px;

    .checkbox {
      .file-unchecked-icon {
        display: block;
      }

      .file-checked-icon {
        display: none;
      }
    }

    .menu {
      margin-left: auto;
    }
  }

  .player-icon {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 32px;
    height: 32px;
    padding: 16px;
    box-sizing: content-box;
    transform: translate(-50%, -50%);
  }

  &:hover {
    .tool-box {
      display: flex;
    }
  }
}

@media screen and (max-width: 768px) {
  .file-item-flow {
    .header-info {
      padding: 11px 16px;
      padding-right: 52px;

      &-box {
        &__name {
          word-break: break-all;
        }
      }
    }

    .tool-box {
      display: flex;
    }
  }
}
</style>
